import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button.js";

export class SessionUnavailableError extends Error {
  constructor() { super("The server is unavailable. Try again shortly."); this.name = "SessionUnavailableError"; }
}

export const AUTH_SYNC_KEY = "vendistri.authSync";

/** Verify the shared cookie independently of the currently selected account. */
export function SessionBoundary({ identity, readIdentity, onChange, children }: {
  identity: string | null | undefined;
  readIdentity: (signal: AbortSignal) => Promise<string | null>;
  onChange: () => void;
  children: ReactNode;
}) {
  const latest = useRef({ identity, readIdentity, onChange });
  latest.current = { identity, readIdentity, onChange };
  const [checking, setChecking] = useState(false);
  const [changed, setChanged] = useState(false);
  const [failed, setFailed] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const retry = useRef<() => void>(() => {});
  const pendingVerification = useRef(false);
  useEffect(() => {
    if (identity !== undefined && pendingVerification.current) {
      pendingVerification.current = false;
      retry.current();
    }
  }, [identity]);
  useEffect(() => {
    let controller: AbortController | null = null;
    let generation = 0;
    let replaced = false;
    const verify = (supersede = false) => {
      const expected = latest.current.identity;
      if (expected === undefined) { pendingVerification.current = true; return; }
      if (replaced || (controller && !supersede)) return;
      controller?.abort();
      const request = new AbortController();
      controller = request;
      const version = ++generation;
      setChecking(true);
      setFailed(false);
      void latest.current.readIdentity(request.signal).then((current) => {
        if (request.signal.aborted || version !== generation) return;
        // A local sign-in may have completed during this request.
        if (latest.current.identity !== expected) { setChecking(false); return; }
        if (current !== expected) {
          replaced = true;
          setChanged(true);
          latest.current.onChange();
          return; // Conceal a confirmed old identity until navigation completes.
        }
        setChecking(false);
      }).catch((error: unknown) => {
        if (!request.signal.aborted && version === generation) {
          setUnavailable(error instanceof SessionUnavailableError);
          setFailed(true);
        }
      }).finally(() => {
        if (version === generation) { controller = null; setChecking(false); }
      });
    };
    const focus = () => verify();
    const visibility = () => { if (document.visibilityState === "visible") verify(); };
    const storage = (event: StorageEvent) => { if (event.key === AUTH_SYNC_KEY || event.key === null) verify(true); };
    retry.current = () => verify(true);
    window.addEventListener("focus", focus);
    window.addEventListener("pageshow", focus);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("storage", storage);
    return () => {
      generation++;
      controller?.abort();
      window.removeEventListener("focus", focus);
      window.removeEventListener("pageshow", focus);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("storage", storage);
    };
  }, []);
  // Verification is silent. Keep drafts and layout mounted, while preventing
  // mutations with an identity that has not yet been verified.
  return <>
    <div ref={(node) => { node?.toggleAttribute("inert", checking || failed || changed); }} aria-hidden={changed || undefined} style={changed ? { visibility: "hidden" } : undefined}>{children}</div>
    {failed ? <div role="alert" style={{ position: "fixed", top: "calc(env(safe-area-inset-top, 0px) + 1rem)", left: "50%", transform: "translateX(-50%)", zIndex: 2147483647, display: "flex", alignItems: "center", gap: ".5rem", width: "max-content", maxWidth: "calc(100% - 2rem)", padding: ".375rem .625rem", border: "1px solid rgb(var(--color-border, 220 225 230))", borderRadius: ".5rem", background: "rgb(var(--color-bg, 255 255 255))", fontSize: ".75rem", lineHeight: 1.4 }}>
      <span>{unavailable ? "Vendistri is unreachable. Your draft stays open; retry when the connection returns." : "Your account could not be verified. Retry to continue."}</span>
      <Button style={{ fontSize: "inherit", minHeight: "2rem", padding: ".25rem .625rem", flexShrink: 0 }} onClick={() => retry.current()}>Retry</Button>
    </div> : null}
  </>;
}
